// GET /me response (AUTH-001 + ORG-001, API_CONTRACTS §3.1). Lead-owned: the identity module
// serves it, the tenancy module supplies `memberships` through IdentityHooks, and the admin shell
// consumes it. Both sides import this file so the shapes cannot drift apart again.

/** System role keys (API_CONTRACTS §4). Custom roles carry their own key and `preset: null`. */
export const ROLE_PRESETS = [
  "owner",
  "manager",
  "cashier",
  "order_manager",
  "inventory",
  "marketing",
  "accountant",
  "viewer",
] as const;
export type RolePreset = (typeof ROLE_PRESETS)[number];

/** Fixed IDs of the system roles seeded by migration control/0102 (same order as ROLE_PRESETS). */
export const SYSTEM_ROLE_IDS: Readonly<Record<RolePreset, `rol_${string}`>> = Object.freeze({
  owner: "rol_00000000000000000000000001",
  manager: "rol_00000000000000000000000002",
  cashier: "rol_00000000000000000000000003",
  order_manager: "rol_00000000000000000000000004",
  inventory: "rol_00000000000000000000000005",
  marketing: "rol_00000000000000000000000006",
  accountant: "rol_00000000000000000000000007",
  viewer: "rol_00000000000000000000000008",
});

export const isRolePreset = (key: string): key is RolePreset =>
  (ROLE_PRESETS as readonly string[]).includes(key);

export interface MeUser {
  id: string; // usr_…
  display_name: string;
  email: string | null;
  phone: string | null;
  locale: "en";
  is_platform_staff: boolean;
  mfa: {
    /** A confirmed TOTP authenticator exists. */
    totp: boolean;
    /** Registered passkeys. */
    passkeys: number;
    /** Time of the session's last MFA (step-up window), ISO 8601. */
    at: string | null;
  };
}

export interface MeStore {
  id: string; // str_…
  slug: string;
  name: string;
}

export interface MeMembership {
  /** `verified` (D-068): Adyvance approved the business verification (the blue tick). */
  org: { id: string; slug: string; name: string; verified?: boolean };
  role: { id: string; key: string; preset: RolePreset | null; name: string };
  /** Resolved permission keys for this org, e.g. "orders.read". The server still enforces. */
  permissions: string[];
  plan: { id: string; key: string; name: string };
  /** Stores the member may open (store-scoped members see only their scopes). */
  stores: MeStore[];
  /** D-065: set while Adyvance has suspended the business (the admin is read-only). */
  suspended?: { reason: string | null; at: string | null };
}

export interface MeResponse {
  user: MeUser;
  memberships: MeMembership[];
  /** True when the user has no active membership: the admin offers business setup. */
  needs_org: boolean;
  /** D-065: this session is Adyvance support's read-only view of one shop, until `expires_at`. */
  support?: { grant_id: string; org_id: string; expires_at: string };
}
